import { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Dropdown, Layout, Menu, Popover, Tooltip, Typography } from "antd";
import {
  BellOutlined,
  BookOutlined,
  BulbOutlined,
  CustomerServiceOutlined,
  ExperimentOutlined,
  FileTextOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  QuestionCircleOutlined,
  ReadOutlined,
} from "@ant-design/icons";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import FontSizeToggle from "./FontSizeToggle";
import FullscreenToggle from "./FullscreenToggle";
import ProfileMenu from "./ProfileMenu";
import UnitSelector from "./UnitSelector";
import {
  NAV_CLOSED_BY_DEFAULT,
  NAV_OPEN_KEYS,
  NAV_SECTIONS,
  READY_ROUTES,
  navKeyForPath,
} from "./navigation";
import { notifications } from "@/api";
import PlanningStatusBar from "@/components/PlanningStatusBar";
import { useApi, useCurrentUser, usePermission, useUnit } from "@/hooks";
import { selectSiderCollapsed, selectTheme, toggleSider, toggleTheme } from "@/app/uiSlice";

const { Content, Sider } = Layout;
const { Text } = Typography;

/** Severity → the dot colour on an alert row. Matches the server's
 *  info / warn / crit vocabulary (core/notifications.py). */
const SEVERITY_COLOR = {
  crit: "var(--ant-color-error, #ff4d4f)",
  warn: "var(--ant-color-warning, #faad14)",
  info: "var(--ant-color-info, #1677ff)",
};

/**
 * Where each alert kind deep-links. Derived from the alert's `kind` — the
 * screen where the condition is worked, which is not always the screen the
 * server's own `link` field names (batch-late carries "/"). A kind with no
 * natural target simply renders unlinked.
 */
const ALERT_KIND_LINKS = {
  "batch-late": "/planner",
  "actuals-missing": "/execution",
  "adherence-low": "/reconciliation",
  "qualification-expiring": "/workforce",
  "material-short": "/recipes",
};
const alertTarget = (a) => ALERT_KIND_LINKS[a.kind] ?? a.link ?? null;

/** The Help dropdown and the bell share this: the four help surfaces. */
const HELP_ITEMS = [
  { key: "/guide", icon: <BookOutlined />, label: "Documentation" },
  { key: "/help/tutorials", icon: <ReadOutlined />, label: "Tutorials" },
  { key: "/help/support", icon: <CustomerServiceOutlined />, label: "Support" },
  { key: "/help/release-notes", icon: <FileTextOutlined />, label: "Release notes" },
];

export default function AppLayout() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // A workflow sub-route keeps its parent row lit: on
  // /variance/investigation/42 the selected item is still "Variance".
  const selectedKey = navKeyForPath(pathname);
  const collapsed = useSelector(selectSiderCollapsed);
  const mode = useSelector(selectTheme);
  const unit = useUnit();
  const user = useCurrentUser();
  const { can, ready: grantsReady } = usePermission();

  // Whether a permission-gated sider entry is shown. Menu placement is its
  // own row (`menu:<page>`) rather than the page resource's `read`, because
  // reads are open to every role here — the entry is a shortcut, not a lock,
  // and the page stays reachable by URL either way.
  //
  // `can()` answers true while grants are still absent — right for a button
  // the server guards anyway, wrong here: these entries are hidden by
  // default, so an older token without grants would reveal them to every
  // role. Until the grants arrive we show nothing, then settle on the
  // server's answer.
  const canSee = (page) => grantsReady && can(`menu:${page}`, "read");

  // The alert count in the header; scoped to the caller's role by the server.
  const { data: alerts } = useApi(
    () => notifications.getNotifications(unit),
    [unit],
    { skip: !unit },
  );
  const alertList = alerts?.alerts ?? [];
  const alertCount = alertList.length;
  const [alertsOpen, setAlertsOpen] = useState(false);

  // The event stream: things that HAPPENED and concern this user, with
  // read state — the "For you" section. Distinct from the computed alerts
  // ("Watchouts"), which describe what is wrong NOW and clear themselves.
  const stream = useApi(() => notifications.getFeed(), []);
  const events = stream.data?.events ?? [];
  const unread = stream.data?.unread ?? 0;
  useEffect(() => {
    // Refresh on open, so the bell shows what arrived since sign-in
    // without a page-wide polling loop.
    if (alertsOpen) stream.reload();
  }, [alertsOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  const openEvent = (e) => {
    setAlertsOpen(false);
    if (!e.read_at)
      notifications.markRead({ ids: [e.id] }).then(stream.reload, () => {});
    if (e.link) navigate(e.link);
  };
  // Severity filter for the popover list only — a chip toggles its severity
  // on/off; the badge count stays the full list, so nothing is hidden from
  // the header. Counts come from the live list, never hardcoded.
  const [alertSeverity, setAlertSeverity] = useState(null);
  const severityCounts = alertList.reduce(
    (acc, a) => ({ ...acc, [a.severity]: (acc[a.severity] ?? 0) + 1 }),
    {},
  );
  const visibleAlerts = alertSeverity
    ? alertList.filter((a) => a.severity === alertSeverity)
    : alertList;

  // Section open/closed state survives reloads; default = everything open.
  const [openSections, setOpenSections] = useState(() => {
    try {
      // v8: keyed per IA generation. Bumping it on every restructure is the
      // point — a saved v2 list knows nothing of the Resources and Planning
      // Rules groups, so reusing it would leave both collapsed behind a
      // caret on the very release that introduces them. v4-v7 change the
      // DEFAULT rather than the shape (only Planning starts open now), and
      // any earlier saved list would override it forever for everyone who
      // has ever loaded the app.
      const saved = JSON.parse(localStorage.getItem("ebmr.navOpen.v8"));
      if (Array.isArray(saved)) return saved;
    } catch {
      /* fall through to default */
    }
    return NAV_OPEN_KEYS;
  });
  const onOpenSections = (keys) => {
    setOpenSections(keys);
    localStorage.setItem("ebmr.navOpen.v8", JSON.stringify(keys));
  };

  /**
   * The rows of one level a user may actually see, groups included.
   *
   * A group carries no `perm` of its own — its visibility is its children's.
   * Filtering only the top level left a group whose every page was withheld
   * rendering as a caret over nothing (Analytics for a role granted neither
   * `reports` nor `variance`), which is the same bare-heading problem the
   * section filter below already solves.
   */
  const visibleRows = (items) =>
    items
      .map((item) =>
        item.group ? { ...item, items: visibleRows(item.items) } : item,
      )
      .filter((item) =>
        item.group
          ? item.items.length > 0
          : !item.perm || canSee(item.perm),
      );

  /**
   * One sider row. A GROUP ("Resources", "Planning Rules") is a nav-only
   * container: it renders as a nested submenu of its pages and is never
   * itself selectable, because it has no route to go to.
   */
  const menuRow = (item) => {
    if (item.group) {
      return {
        key: item.group,
        // No manual Tooltip on the collapsed rail: antd's inlineCollapsed
        // menu already pops the children flyout (and a label tooltip for
        // leaf rows) on hover — a second Tooltip renders on top of it as
        // two overlapping menus.
        icon: <span className="inline-flex items-center">{item.icon}</span>,
        label: <span className="text-[15px]">{item.label}</span>,
        children: item.items.map(menuRow),
      };
    }
    const ready = READY_ROUTES.has(item.key);
    const active = selectedKey === item.key;
    return {
      key: item.key,
        // The lucide glyph already carries its size and bold stroke from
        // navigation.jsx. It takes no colour of its own — it inherits the
        // row's, so it goes accent when selected. Collapsed, antd names the
        // row itself via its built-in hover tooltip (`title`), so the icon
        // needs no wrapper of its own.
        title: item.label,
        icon: <span className="inline-flex items-center">{item.icon}</span>,
        // Pages not yet migrated stay visible but inert, so the nav never
        // promises a screen that is not there.
        disabled: !ready,
        // The selected row inverts out of the dark drawer: a solid white
        // chip, its label and icon lettered blue, behind the 3px bar that
        // marks the left edge. One selection colour for the whole nav.
        style: active
          ? {
              borderLeft: `3px solid var(--nav-selected-fg)`,
              background: "var(--nav-selected-bg)",
              color: "var(--nav-selected-fg)",
            }
          : { borderLeft: "3px solid transparent" },
        // Rows read at regular weight; only the active row is emphasized
        // (rule in styles/index.css keyed on .ant-menu-item-selected).
        label: ready ? (
          <span className="text-[15px]">{item.label}</span>
        ) : (
          <Tooltip title="Not migrated yet" placement="right">
            <span className="text-[15px]">{item.label}</span>
          </Tooltip>
        ),
    };
  };

  /** The open-keys default for THIS user: every collapsible key their role
   *  can actually see, minus the long sections that start closed. A section
   *  whose every page is withheld renders nothing, so naming it here would
   *  put a key in the open list for a submenu that does not exist. */
  const defaultOpenKeys = useMemo(
    () =>
      NAV_SECTIONS.flatMap((section) => {
        const rows = visibleRows(section.items);
        if (rows.length === 0) return [];
        return [
          ...(NAV_CLOSED_BY_DEFAULT.has(section.key) ? [] : [section.key]),
          ...rows.filter((r) => r.group).map((r) => r.group),
        ];
      }),
    // visibleRows closes over canSee, which is derived from the user's role
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canSee],
  );

  // Re-expanding the rail starts the sider from its default shape rather
  // than from whatever was open when it was collapsed. The long sections
  // (Planning, Master data) are the reason: left open across a collapse the
  // sider comes back as a scroll, and the user has to close them by hand
  // every time to see the map again. Their own toggles still persist for
  // the rest of the session — this resets only on the collapse→expand edge.
  const wasCollapsed = useRef(collapsed);
  useEffect(() => {
    if (wasCollapsed.current && !collapsed) {
      setOpenSections(defaultOpenKeys);
      localStorage.setItem("ebmr.navOpen.v8", JSON.stringify(defaultOpenKeys));
    }
    wasCollapsed.current = collapsed;
  }, [collapsed, defaultOpenKeys]);

  const menuItems = NAV_SECTIONS.map((section) => ({
    key: section.key,
    // Collapsed to the 80px rail there is no room for headings, so the
    // sections flatten to groups; open, each section is a collapsible
    // submenu — the small caps heading with antd's caret.
    label: collapsed ? null : (
      <span
        className="font-semibold uppercase tracking-wider text-white/50"
        style={{ fontSize: 11 }}
      >
        {section.label}
      </span>
    ),
    type: collapsed ? "group" : undefined,
    // An item may name the resource whose `read` grant shows it (the page
    // itself stays routed for everyone — this hides the entry, not the
    // door). Editable in Configuration → Access control, so granting a role
    // the page there puts it in their sider on next sign-in.
    children: visibleRows(section.items).map(menuRow),
  }))
    // A section whose every row is hidden would otherwise render as a bare
    // heading with nothing under it — ADMINISTRATION is exactly that for a
    // supervisor. Drop the heading with its rows.
    .filter((section) => section.children.length > 0);

  return (
    <Layout className="min-h-screen">
      {/* 260 / 80 — the BMR_Frontend drawer widths, so the two products
          line up when they sit side by side. */}
      <Sider
        collapsible
        collapsed={collapsed}
        collapsedWidth={80}
        trigger={null}
        theme="dark"
        width={260}
        /* sider-flex makes antd's inner .ant-layout-sider-children wrapper a
           flex column too — without it the Menu has no bounded height, so it
           can neither shrink nor scroll and simply overflows. */
        className="sider-flex"
        style={{
          /* Fixed, not sticky: the expanded menu floats OVER the page as an
             overlay drawer instead of pushing the body narrower. The body
             below reserves only the 80px rail; the scrim behind the expanded
             state dims the page and collapses the menu on click. */
          position: "fixed",
          left: 0,
          top: 0,
          zIndex: 40,
          height: "100vh",
          // No scrollbar: the nav is sized to fit the viewport instead. The
          // rows below flex down rather than overflow, and MiniDrawerStyled.js
          // keeps the same hairline right edge and shadow in both states.
          overflow: "hidden",
          borderRight: "1px solid rgba(255,255,255,0.12)",
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          transition:
            "width 0.4s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.4s ease",
        }}
      >
        {/* DrawerHeader/index.js: a gradient logo tile beside the product
            name, collapsing to just the initials on the icon rail. The block
            is centred in the drawer on a single 4px gutter either side.

            Every size here is in px, not rem, deliberately. The font-size
            toggle steps the root font-size (12–18px), which every rem-based
            size follows — the brand would swell by half again between Small
            and Extra Large and stop fitting the 260px row. It is a fixed
            piece of chrome, so it holds its size while the pages rescale. */}
        <div className="flex shrink-0 items-center justify-center gap-3 px-1 py-4">
          {collapsed ? (
            <span
              className="font-bold text-white"
              style={{ fontSize: 22, lineHeight: 1.2 }}
            >
              N
            </span>
          ) : (
            <>
              <div
                className="flex shrink-0 items-center justify-center rounded-lg shadow-lg"
                style={{
                  height: 44,
                  width: 44,
                  background:
                    "linear-gradient(to bottom right, var(--accent-border), var(--accent-hover))",
                }}
              >
                <ExperimentOutlined style={{ fontSize: 24, color: "#fff" }} />
              </div>
              {/* min-w-0 so a long product name ellipsises inside the row
                  rather than pushing the block wider than the drawer. */}
              <div className="min-w-0">
                <h1
                  className="m-0 truncate font-bold tracking-tight text-white"
                  style={{ fontSize: 19, lineHeight: 1.25 }}
                >
                  eBMR Scheduler
                </h1>
                <p
                  className="m-0 truncate font-medium text-white/60"
                  style={{ fontSize: 12, lineHeight: 1.3 }}
                >
                  Nexgensis
                </p>
              </div>
            </>
          )}
        </div>

        {/* min-h-0 lets this shrink inside the flex column instead of
            forcing the sider taller than the viewport. The nav is sized to
            fit at a normal window height, so no scrollbar shows; `auto`
            rather than `hidden` only so a very short window degrades to
            scrolling instead of hiding entries outright. */}
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedKey]}
          openKeys={collapsed ? undefined : openSections}
          onOpenChange={onOpenSections}
          items={menuItems}
          onClick={({ key }) => {
            navigate(key);
            // The expanded menu is an overlay; once a destination is picked
            // it folds back to the rail so the page it covered is readable.
            if (!collapsed) dispatch(toggleSider());
          }}
          className="min-h-0 flex-1 no-scrollbar"
          style={{
            borderInlineEnd: "none",
            background: "transparent",
            overflowY: "auto",
          }}
        />
      </Sider>

      {/* Click-away scrim behind the expanded overlay menu: dims the page it
          covers and folds the menu back to the rail. Rendered only while
          expanded, so the rail state costs nothing. */}
      {!collapsed && (
        <div
          aria-hidden="true"
          onClick={() => dispatch(toggleSider())}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 39,
            background: "rgba(15, 23, 42, 0.35)",
          }}
        />
      )}

      {/* min-w-0 lets this column actually shrink to the space the rail
          leaves it. Without it a wide row inside (a table, the Gantt) sets a
          large min-content width on the flex item, the column grows past the
          viewport, and the header — sized to that column — stops lining up
          with the row it heads. The margin reserves the FIXED sider's rail
          width only: expanding the menu overlays the page, never reflows it. */}
      <Layout
        className="min-w-0"
        style={{ background: "var(--page)", marginLeft: 80 }}
      >
        {/* 64px surface header with a hairline rule, as in BMR_Frontend —
            it separates the chrome from the page without a shadow. */}
        <div
          /* gap-2 = the 8px HeaderContent/index.js puts between its controls.
             w-full so the bar spans the whole column rather than shrinking to
             its controls. */
          className="sticky top-0 z-20 flex w-full items-center gap-2 px-6"
          style={{
            height: 64,
            flexShrink: 0,
            background: "var(--surface)",
            borderBottom: "1px solid var(--border)",
          }}
        >
          {/* The drawer toggle lives at the header's left edge, as in
              BMR_Frontend's MainLayout — not tucked under the nav. */}
          <Tooltip title={collapsed ? "Expand menu" : "Collapse menu"}>
            <Button
              type="text"
              shape="circle"
              className="header-icon-btn"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => dispatch(toggleSider())}
              aria-label={collapsed ? "Expand menu" : "Collapse menu"}
            />
          </Tooltip>

          <div className="ml-auto" />
          <UnitSelector />
          {/* HeaderContent/index.js's desktop order: language, theme, font
              size, full screen, then notifications and the profile. */}
          {/* LanguageSelector deliberately unmounted: it recorded a choice and
              translated nothing — a control that does nothing teaches users the
              header lies. The component and uiSlice hook stay; remount here the
              day a translation catalogue is wired. */}
          <Tooltip title={mode === "dark" ? "Switch to light" : "Switch to dark"}>
            <Button
              type="text"
              shape="circle"
              className="header-icon-btn"
              icon={<BulbOutlined />}
              onClick={() => dispatch(toggleTheme())}
              aria-label="Toggle theme"
            />
          </Tooltip>
          <FontSizeToggle />
          <FullscreenToggle />
          <Dropdown
            trigger={["click"]}
            placement="bottomRight"
            menu={{
              items: HELP_ITEMS.map((i) => ({ ...i })),
              onClick: ({ key }) => navigate(key),
            }}
          >
            <Button
              type="text"
              shape="circle"
              className="header-icon-btn"
              icon={<QuestionCircleOutlined />}
              aria-label="Help"
            />
          </Dropdown>
          <Popover
            trigger="click"
            placement="bottomRight"
            open={alertsOpen}
            onOpenChange={setAlertsOpen}
            overlayStyle={{ paddingTop: 9 }}
            content={
              <div style={{ width: 340 }}>
                <div
                  className="flex items-baseline justify-between pb-2"
                  style={{ borderBottom: "1px solid var(--border)" }}
                >
                  <span className="text-sm font-semibold">
                    For you{unread > 0 ? ` · ${unread} unread` : ""}
                  </span>
                  {unread > 0 && (
                    <Button
                      type="link"
                      size="small"
                      className="px-0 text-[11px]"
                      onClick={() =>
                        notifications
                          .markRead({ all: true })
                          .then(stream.reload, () => {})
                      }
                    >
                      Mark all read
                    </Button>
                  )}
                </div>
                {events.length === 0 ? (
                  <div className="py-3 text-center">
                    <Text type="secondary" className="text-xs">
                      Nothing yet — plan decisions, holds, breakdowns and
                      approvals that concern you will land here.
                    </Text>
                  </div>
                ) : (
                  <div style={{ maxHeight: 200, overflowY: "auto" }}>
                    {events.map((e) => (
                      <div
                        key={e.id}
                        role="link"
                        tabIndex={0}
                        className="cursor-pointer rounded px-1 hover:bg-[var(--icon-btn-bg-hover)]"
                        style={{ borderBottom: "1px solid var(--border)" }}
                        onClick={() => openEvent(e)}
                        onKeyDown={(ev) => ev.key === "Enter" && openEvent(e)}
                      >
                        <div className="flex items-start gap-2 py-2">
                          {/* Unread dot: accent when new, hollow once seen. */}
                          <span
                            className="mt-1.5 shrink-0 rounded-full"
                            style={{
                              height: 8,
                              width: 8,
                              background: e.read_at
                                ? "transparent"
                                : "var(--accent)",
                              border: e.read_at
                                ? "1px solid var(--border)"
                                : "none",
                            }}
                          />
                          <span className="min-w-0 text-xs leading-relaxed">
                            <span
                              className={e.read_at ? "" : "font-semibold"}
                              style={{ color: "var(--text)" }}
                            >
                              {e.title}
                            </span>
                            {e.body && (
                              <span
                                className="block text-[11px]"
                                style={{ color: "var(--text-secondary)" }}
                              >
                                {e.body}
                              </span>
                            )}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div
                  className="mt-2 pb-2"
                  style={{ borderBottom: "1px solid var(--border)" }}
                >
                  <span className="text-sm font-semibold">Watchouts</span>
                  <Text type="secondary" className="text-xs">
                    {" "}
                    · live from the current plan and feeds
                  </Text>
                </div>
                {alertList.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5 py-2">
                    {["info", "warn", "crit"].map((sev) => {
                      const active = alertSeverity === sev;
                      const count = severityCounts[sev] ?? 0;
                      return (
                        <button
                          key={sev}
                          type="button"
                          disabled={!count && !active}
                          onClick={() =>
                            setAlertSeverity(active ? null : sev)
                          }
                          className="flex cursor-pointer items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px]"
                          style={{
                            border: `1px solid ${
                              active ? SEVERITY_COLOR[sev] : "var(--border)"
                            }`,
                            background: active
                              ? "var(--icon-btn-bg-hover)"
                              : "transparent",
                            color: "var(--text)",
                            opacity: count || active ? 1 : 0.45,
                          }}
                        >
                          <span
                            className="rounded-full"
                            style={{
                              height: 7,
                              width: 7,
                              background: SEVERITY_COLOR[sev],
                            }}
                          />
                          {sev} ({count})
                        </button>
                      );
                    })}
                    {alertSeverity && (
                      <Button
                        type="link"
                        size="small"
                        className="px-1 text-[11px]"
                        onClick={() => setAlertSeverity(null)}
                      >
                        all
                      </Button>
                    )}
                  </div>
                )}
                {alertList.length === 0 ? (
                  <div className="py-6 text-center">
                    <Text type="secondary" className="text-xs">
                      Nothing needs attention right now.
                    </Text>
                  </div>
                ) : visibleAlerts.length === 0 ? (
                  <div className="py-6 text-center">
                    <Text type="secondary" className="text-xs">
                      No {alertSeverity} alerts right now.
                    </Text>
                  </div>
                ) : (
                  <div style={{ maxHeight: 320, overflowY: "auto" }}>
                    {visibleAlerts.map((a) => {
                      const to = alertTarget(a);
                      const row = (
                        <div className="flex items-start gap-2 py-2">
                          <span
                            className="mt-1.5 shrink-0 rounded-full"
                            style={{
                              height: 8,
                              width: 8,
                              background: SEVERITY_COLOR[a.severity] ?? "var(--text-muted)",
                            }}
                          />
                          <span
                            className="min-w-0 text-xs leading-relaxed"
                            style={{ color: "var(--text)" }}
                          >
                            {a.text}
                          </span>
                        </div>
                      );
                      return to ? (
                        <div
                          key={a.id}
                          role="link"
                          tabIndex={0}
                          className="cursor-pointer rounded px-1 hover:bg-[var(--icon-btn-bg-hover)]"
                          style={{ borderBottom: "1px solid var(--border)" }}
                          onClick={() => {
                            setAlertsOpen(false);
                            navigate(to);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              setAlertsOpen(false);
                              navigate(to);
                            }
                          }}
                        >
                          {row}
                        </div>
                      ) : (
                        <div
                          key={a.id}
                          className="px-1"
                          style={{ borderBottom: "1px solid var(--border)" }}
                        >
                          {row}
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="flex items-center justify-between gap-2 pt-2">
                  {/* Alerts are derived live from the plan and feeds — not an
                      inbox, so there is nothing to mark as read. */}
                  <Text type="secondary" className="text-[11px]">
                    Live-derived — alerts clear when their condition clears.
                  </Text>
                  <Button
                    type="link"
                    size="small"
                    className="px-0"
                    onClick={() => {
                      setAlertsOpen(false);
                      navigate("/");
                    }}
                  >
                    Open Planning Dashboard
                  </Button>
                </div>
              </div>
            }
          >
            <Badge count={unread + alertCount} size="small" offset={[-2, 2]}>
              <Button
                type="text"
                shape="circle"
                className="header-icon-btn"
                icon={<BellOutlined />}
                aria-label="Alerts"
              />
            </Badge>
          </Popover>
          <ProfileMenu />
        </div>

        {/* The persistent planning status strip: schedule health, commit
            state, plan size, last optimization and connector status in one
            30px line, sticky right under the header. */}
        <PlanningStatusBar />

        {/* PageShell owns the page padding. Adding any here too would make
            the sides thicker than the top, which is what it looked like
            before. */}
        {/* Wide pages scroll within the content area instead of widening the
            column out from under the header. */}
        {/* Bottom padding keeps the last of the page clear of the fixed
            footer. */}
        <Content className="min-w-0" style={{ overflowX: "auto", paddingBottom: 48 }}>
          <Outlet />
        </Content>

        {/* Fixed to the viewport bottom; left edge clears the rail. The
            expanded menu overlays rather than reflows, so the footer never
            moves with it. */}
        <footer
          className="flex flex-wrap justify-between gap-2 px-6 py-3 text-[11px]"
          style={{
            position: "fixed",
            bottom: 0,
            right: 0,
            left: 80,
            zIndex: 10,
            background: "var(--surface)",
            color: "var(--text-muted)",
            borderTop: "1px solid var(--border)",
          }}
        >
          <span>
            © 2026 Nexgensis Technologies · All rights reserved · eBMR Scheduler
            v0.9
          </span>
          <span className="flex flex-wrap items-center justify-end gap-x-2">
            <span>
              21 CFR Part 11 · EU Annex 11 — planning metadata only; GMP
              execution stays in the eBMR
            </span>
            <span aria-hidden="true">·</span>
            {/* Draft legal notices (features/legal) — muted, same size as the
                compliance line; separators between, none trailing. */}
            {[
              ["Privacy", "/legal/privacy"],
              ["Terms", "/legal/terms"],
              ["Legal", "/legal/imprint"],
            ].map(([label, path], i, arr) => (
              <span key={path} className="flex items-center gap-x-2">
                <a
                  href={path}
                  style={{ color: "inherit", textDecoration: "underline" }}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(path);
                  }}
                >
                  {label}
                </a>
                {i < arr.length - 1 && <span aria-hidden="true">·</span>}
              </span>
            ))}
          </span>
        </footer>
      </Layout>
    </Layout>
  );
}
